import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import NontAuthorized401 from "NontAuthorized401";
import Pagination from "components/pagination";
import { clearNotificationHistory, getNotificationHistory } from "./utils/historyStorage";
import { USE_NOTIFICATION_DEMO_DATA } from "./utils/mockData";

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getEditRoute = (record) => (record?.channel === "sms" ? "/sms-notification" : "/email-notification");

const NotificationList = () => {
  const hasToken = Boolean(localStorage.getItem("Token"));
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);

  const refreshRecords = () => {
    setRecords(getNotificationHistory());
  };

  useEffect(() => {
    refreshRecords();
    const onFocus = () => refreshRecords();
    const onStorage = () => refreshRecords();
    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const filteredRecords = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return records.filter((record) => {
      const matchesChannel = channelFilter === "all" || record?.channel === channelFilter;
      const text = `${record?.target || ""} ${record?.subject || ""} ${record?.message || ""}`.toLowerCase();
      const matchesSearch = !keyword || text.includes(keyword);
      return matchesChannel && matchesSearch;
    });
  }, [records, search, channelFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / recordsPerPage));
  const paginatedRecords = filteredRecords.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleClearAll = () => {
    clearNotificationHistory();
    setRecords([]);
    setCurrentPage(1);
  };

  if (!hasToken) {
    return <NontAuthorized401 />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Notification List</h3>
            <p className="text-sm text-slate-500">Sent email and SMS campaign records</p>
            {USE_NOTIFICATION_DEMO_DATA ? (
              <span className="mt-2 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                Demo history seeded locally
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleClearAll}
            className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
          >
            Clear All
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 md:w-72"
            placeholder="Search target / subject / message"
          />
          <select
            value={channelFilter}
            onChange={(event) => {
              setChannelFilter(event.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2">Sent At</th>
                <th className="px-3 py-2">Channel</th>
                <th className="px-3 py-2">Module</th>
                <th className="px-3 py-2">Target</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Recipients</th>
                <th className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedRecords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                    No sent notification records found.
                  </td>
                </tr>
              ) : (
                paginatedRecords.map((record) => (
                  <tr key={record?.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-xs text-slate-600">{formatDateTime(record?.sentAt)}</td>
                    <td className="px-3 py-2 capitalize">{record?.channel || "-"}</td>
                    <td className="px-3 py-2 capitalize">{record?.module || "-"}</td>
                    <td className="px-3 py-2">{record?.target || "-"}</td>
                    <td className="px-3 py-2 truncate max-w-[280px]">{record?.subject || "-"}</td>
                    <td className="px-3 py-2">{record?.totalRecipients || 0}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/notification-list/${record?.id}`)}
                          className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          View
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate(getEditRoute(record), { state: { editRecord: record } })}
                          className="rounded-md border border-blue-300 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filteredRecords.length > 0 ? (
          <div className="mt-2 rounded-lg border border-slate-200">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              recordsPerPage={recordsPerPage}
              onRecordsPerPageChange={(value) => {
                setRecordsPerPage(value);
                setCurrentPage(1);
              }}
              totalRecords={filteredRecords.length}
              count={paginatedRecords.length}
              showFirstLastControls
              compact
            />
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
};

export default NotificationList;
